import React, { useCallback } from 'react';
import Button from '../../DesignSystem/button';
import Input from '../../DesignSystem/input';
import { EditBandwidthFieldMapping } from '../../../utils/appConstants';
import { wrapButtonInTooltip } from '../../../utils/methods';

interface EditBandwidthFormProps {
  bandwidth: any;
  nearend_name: string;
  nearend_description: string;
  farend_name: string;
  farend_description: string;
  description: string;
  loading: boolean;
  cancelLoading: boolean;
  neError: string;
  feError: string;
  portView: boolean;
  onClose: () => void;
  submitEdits: () => void;
  deleteOffNetBandwidth: () => void;
  onFieldChange: (e: any, value: string) => void;
  handleCancelSubscription: (port: any) => void;
}

const EditBandwidthForm: React.FC<EditBandwidthFormProps> = ({
  onClose, cancelLoading, loading, portView, handleCancelSubscription, deleteOffNetBandwidth, onFieldChange, nearend_description, nearend_name, bandwidth, submitEdits, neError, feError, farend_description, farend_name, description,
}) => {
  const handleInputChange = useCallback((e: any, field: string) => {
    onFieldChange(field, e?.target?.value ?? '');
  }, [onFieldChange]);
  const linkViewDisabled = ((description === bandwidth?.bandwidth_description) && (nearend_name === bandwidth?.nearend_port_name) && (nearend_description === bandwidth?.nearend_port_description) && (farend_name === bandwidth?.farend_port_name) && (farend_description === bandwidth?.farend_port_description)) || (nearend_name === '') || (nearend_description === '') || (farend_name === '') || (farend_description === '') || (description === '') || (neError !== '') || (feError !== '');
  const portViewDisabled = (((nearend_name === bandwidth?.port_name) && (nearend_description === bandwidth?.port_description)) || (nearend_name === '') || (nearend_description === '') || (neError !== ''));
  const {
    added_far_cross_connect, added_near_cross_connect, farend_verified, is_verified, nearend_verified, near_requested_verification, far_requested_verification, listing_id, isBought,
  } = bandwidth || {};
  const disableDelete = (added_far_cross_connect || added_near_cross_connect || farend_verified || is_verified || nearend_verified || near_requested_verification || far_requested_verification || listing_id || isBought);
  const showDelete = !(farend_verified || nearend_verified || isBought);
  let tooltip = '';
  if (far_requested_verification || near_requested_verification) {
    tooltip = 'One or both of the bandwidth ports are being moved to WX. Deleting this bandwidth is not permitted.';
  } else if (added_far_cross_connect || added_near_cross_connect) {
    tooltip = 'This bandwidth must be removed from all cross connects before it can be deleted.';
  } else if (listing_id) {
    tooltip = 'This bandwidth listed in the marketplace.';
  }
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      if (portView && !portViewDisabled) {
        submitEdits();
      } if (!portView && !linkViewDisabled) {
        submitEdits();
      }
    }
  }, [linkViewDisabled, portView, portViewDisabled, submitEdits]);

  return (
    !portView ? (
      <div className="edit-port">
        <div className="edit-port-root">
          <div className="title">
            <label>Edit Bandwidth Details</label>
          </div>
          <div className="form">
            <div className="port-option">
              <div className="subtitle">
                <label>Edit Bandwidth Description</label>
              </div>
              <Input value={description} variant="input" onKeypress={handleKeyPress} name={EditBandwidthFieldMapping.description} onChange={(e) => handleInputChange(e, EditBandwidthFieldMapping.description)} />
            </div>
          </div>
          <br />
          <div className="form">
            <div className="port-option">
              <div className="subtitle">
                <label>Edit Nearend Port Description</label>
              </div>
              <Input value={nearend_description} onKeypress={handleKeyPress} variant="input" name={EditBandwidthFieldMapping.portDescriptionNE} onChange={(e) => handleInputChange(e, EditBandwidthFieldMapping.portDescriptionNE)} error={(neError !== '')} />
            </div>
            <div className="port-option">
              <div className="subtitle">
                <label>Edit Nearend Port Name</label>
              </div>
              <Input value={nearend_name} variant="input" onKeypress={handleKeyPress} name={EditBandwidthFieldMapping.portNameNE} onChange={(e) => handleInputChange(e, EditBandwidthFieldMapping.portNameNE)} error={(neError !== '')} />
            </div>
            <span className="error-message">{neError}</span>
          </div>
          <br />
          <div className="form">
            <div className="port-option">
              <div className="subtitle">
                <label>Edit Farend Port Description</label>
              </div>
              <Input value={farend_description} variant="input" onKeypress={handleKeyPress} name={EditBandwidthFieldMapping.portDescriptionFE} onChange={(e) => handleInputChange(e, EditBandwidthFieldMapping.portDescriptionFE)} error={(feError !== '')} />
            </div>
            <div className="port-option">
              <div className="subtitle">
                <label>Edit Farend Port Name</label>
              </div>
              <Input value={farend_name} variant="input" onKeypress={handleKeyPress} name={EditBandwidthFieldMapping.portNameFE} onChange={(e) => handleInputChange(e, EditBandwidthFieldMapping.portNameFE)} error={(feError !== '')} />
            </div>
            <span className="error-message">{feError}</span>
          </div>
          <div className="delete-button">
            {showDelete && wrapButtonInTooltip(
              tooltip,
              <Button variant="close" className="btn cancel-btn" node="DeleteBandwidth" disabled={disableDelete} onClick={() => deleteOffNetBandwidth()} />,
              'bottom',
            )}
          </div>
          {(bandwidth && !bandwidth.status && bandwidth.provider) && (
            <div className="cancel">
              <Button
                variant="close"
                className="btn cancel-btn"
                node="Cancel Subscription"
                loading={cancelLoading}
                onClick={() => {
                  handleCancelSubscription(bandwidth);
                }}
              />
            </div>
          )}

          <div className="buttons">
            <Button variant="close" className="btn cancel-btn" node="Cancel" onClick={onClose} />
            <Button variant="primary" className="btn" node="Submit Edits" disabled={linkViewDisabled} onClick={submitEdits} loading={loading} />
          </div>
        </div>
      </div>
    ) : (
      <div className="edit-port">
        <div className="edit-port-root">
          <div className="title">
            <label>Edit Bandwidth Port</label>
          </div>
          <div className="form">
            <div className="port-option">
              <div className="subtitle">
                <label>Edit Port Description</label>
              </div>
              <Input value={nearend_description} onKeypress={handleKeyPress} variant="input" name={EditBandwidthFieldMapping.portDescriptionNE} onChange={(e) => handleInputChange(e, EditBandwidthFieldMapping.portDescriptionNE)} error={(neError !== '')} />
            </div>
            <div className="port-option">
              <div className="subtitle">
                <label>Edit Port Name</label>
              </div>
              <Input value={nearend_name} variant="input" onKeypress={handleKeyPress} name={EditBandwidthFieldMapping.portNameNE} onChange={(e) => handleInputChange(e, EditBandwidthFieldMapping.portNameNE)} error={(neError !== '')} />
            </div>
            <span className="error-message">{neError}</span>
          </div>
          <div className="buttons">
            <Button variant="close" className="btn cancel-btn" node="Cancel" onClick={onClose} />
            <Button variant="primary" className="btn" node="Submit Edits" disabled={portViewDisabled} onClick={submitEdits} loading={loading} />
          </div>
        </div>
      </div>
    )
  );
};

export default EditBandwidthForm;
